import { useState, useRef, useEffect, useMemo } from 'react';
import { Eye, EyeOff, Copy, Check, MoreHorizontal, Pencil, Trash2, History, GitBranch } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { Secret, SecretType as APISecretType } from '@/hooks/useSecrets';

type DisplaySecretType = 'String' | 'JSON' | 'URL' | 'Number' | 'Boolean' | 'IPv4' | 'IPv6';

// Map API types (lowercase) to display types (capitalized)
const apiTypeToDisplay: Record<APISecretType, DisplaySecretType> = {
  string: 'String',
  json: 'JSON',
  url: 'URL',
  number: 'Number',
  boolean: 'Boolean',
  ipv4: 'IPv4',
  ipv6: 'IPv6',
};

interface SecretCardProps {
  secret: Secret;
  isRevealed: boolean;
  revealedValue?: string;
  isEditing: boolean;
  isLoadingReveal: boolean;
  isSaving: boolean;
  isFocused?: boolean;
  dataIndex?: number;
  onReveal: (key: string) => void;
  onHide: (key: string) => void;
  onCopy: (key: string) => void;
  onEdit: (key: string) => void;
  onSave: (key: string, value: string) => void;
  onCancelEdit: () => void;
  onOpenDrawer: (key: string) => void;
  onDelete: (key: string) => void;
}

// Get display type from API type, with fallback to 'String'
function getDisplayType(apiType: APISecretType | undefined): DisplaySecretType {
  if (!apiType) return 'String';
  return apiTypeToDisplay[apiType] || 'String';
}

function formatJSON(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function isEnvReference(value: string): boolean {
  return /\$\{[^}]+\}/.test(value) || /\$[A-Z_][A-Z0-9_]*/i.test(value);
}

const typeColors: Record<DisplaySecretType, string> = {
  String: 'text-muted-foreground',
  JSON: 'text-amber-400',
  URL: 'text-blue-400',
  Number: 'text-emerald-400',
  Boolean: 'text-violet-400',
  IPv4: 'text-cyan-400',
  IPv6: 'text-teal-400',
};

export function SecretCard({
  secret,
  isRevealed,
  revealedValue,
  isEditing,
  isLoadingReveal,
  isSaving,
  isFocused = false,
  dataIndex,
  onReveal,
  onHide,
  onCopy,
  onEdit,
  onSave,
  onCancelEdit,
  onOpenDrawer,
  onDelete,
}: SecretCardProps) {
  const [editValue, setEditValue] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use type from API (stored in DB), which is available before reveal
  const displayType = useMemo(() => getDisplayType(secret.type), [secret.type]);
  const isJSON = displayType === 'JSON';
  const hasEnvRef = revealedValue ? isEnvReference(revealedValue) : false;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopy(secret.key);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  useEffect(() => {
    if (isEditing) {
      if (isJSON && textareaRef.current) {
        textareaRef.current.focus();
      } else if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [isEditing, isJSON]);

  const prevIsEditing = useRef(false);
  useEffect(() => {
    // Only set edit value when transitioning from not editing to editing
    if (isEditing && !prevIsEditing.current && revealedValue !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEditValue(isJSON ? formatJSON(revealedValue) : revealedValue);
    }
    prevIsEditing.current = isEditing;
  }, [isEditing, revealedValue, isJSON]);

  const handleSave = () => {
    onSave(secret.key, editValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isJSON) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancelEdit();
    }
  };

  const renderValue = () => {
    if (isLoadingReveal) {
      return (
        <span className="text-sm text-muted-foreground flex items-center gap-2">
          <motion.span
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            Decrypting...
          </motion.span>
        </span>
      );
    }

    if (!isRevealed) {
      return (
        <span className="text-sm text-muted-foreground/50 tracking-[0.2em] select-none">
          ••••••••••••
        </span>
      );
    }

    if (!revealedValue) {
      return <span className="text-sm text-muted-foreground/50 italic">empty</span>;
    }

    if (isJSON) {
      return (
        <pre className="text-sm font-mono text-foreground/90 whitespace-pre-wrap break-all leading-relaxed">
          {formatJSON(revealedValue)}
        </pre>
      );
    }

    if (hasEnvRef) {
      return (
        <span className="text-sm font-mono text-primary">
          {revealedValue}
        </span>
      );
    }

    return (
      <motion.span 
        initial={{ opacity: 0, filter: 'blur(4px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.2 }}
        className="text-sm font-mono text-foreground/90 truncate"
      >
        {revealedValue}
      </motion.span>
    );
  };

  return (
    <motion.div
      data-index={dataIndex}
      layout
      className={`group flex border-b border-border/30 last:border-b-0 transition-all duration-200 ${
        isFocused
          ? 'bg-primary/[0.06] ring-1 ring-inset ring-primary/20'
          : 'hover:bg-muted/20'
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3.5 w-[340px] min-w-[340px] border-r border-border/30 bg-muted/[0.03]">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-muted/80 to-muted/40 flex items-center justify-center flex-shrink-0 mt-0.5 border border-border/30">
          <span className="text-[10px] text-muted-foreground font-mono font-medium tracking-tight">
            {secret.key.substring(0, 2).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[13px] font-medium text-foreground truncate">
              {secret.key}
            </span>
            <AnimatePresence>
              {secret.inherited_from && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <Badge
                    variant="outline"
                    className="text-[9px] px-1.5 py-0 h-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1"
                  >
                    <GitBranch className="h-2.5 w-2.5" />
                    inherited
                  </Badge>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="flex items-center gap-2 mt-1">
            {/* Always show type from API (available before reveal) */}
            <span className={`text-[11px] ${typeColors[displayType]}`}>
              {displayType}
            </span>
            {secret.description && (
              <>
                <span className="text-muted-foreground/30">·</span>
                <span className="text-[11px] text-muted-foreground truncate" title={secret.description}>
                  {secret.description}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div 
        className="flex-1 flex items-start px-4 py-3.5 cursor-pointer min-w-0"
        onClick={() => !isEditing && onOpenDrawer(secret.key)}
      >
        {isEditing ? (
          <div className="flex-1 flex items-start gap-2">
            {isJSON ? (
              <textarea
                ref={textareaRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 font-mono text-sm bg-background/50 border border-border/50 rounded-lg px-3 py-2 min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                placeholder="Enter JSON value..."
              />
            ) : (
              <Input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 font-mono text-sm bg-background/50 border-border/50 focus:border-primary/50"
                placeholder="Enter value..."
              />
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="shadow-lg shadow-primary/20"
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-start justify-between gap-3 min-w-0">
            <div className="flex-1 min-w-0 py-0.5">
              {renderValue()}
            </div>
            
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all duration-200 flex-shrink-0">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isRevealed) {
                      onHide(secret.key);
                    } else {
                      onReveal(secret.key);
                    }
                  }}
                  disabled={isLoadingReveal}
                  className="hover:bg-muted/50"
                >
                  {isRevealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={handleCopy}
                  className="hover:bg-muted/50"
                >
                  <AnimatePresence mode="wait">
                    {isCopied ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Check className="h-3.5 w-3.5 text-emerald-400" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Button>
              </motion.div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={(e) => e.stopPropagation()}
                    className="hover:bg-muted/50"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="glass">
                  <DropdownMenuItem onClick={() => onEdit(secret.key)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenDrawer(secret.key)}>
                    <History className="h-4 w-4 mr-2" />
                    View History
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(secret.key)}
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {secret.inherited_from ? 'Hide' : 'Delete'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
